import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from '@shared/components/ui/Toaster';

interface DegradedModeContextType {
  isDegraded: boolean;
  gracePeriodRemaining: number;
  enterDegradedMode: (reason: string) => void;
  exitDegradedMode: () => void;
  canPerformWrite: boolean;
  canAccessSensitiveData: boolean;
  degradedReason: string | null;
}

const DegradedModeContext = createContext<DegradedModeContextType>({
  isDegraded: false,
  gracePeriodRemaining: 0,
  enterDegradedMode: () => {},
  exitDegradedMode: () => {},
  canPerformWrite: false,
  canAccessSensitiveData: false,
  degradedReason: null,
});

export const useDegradedMode = () => useContext(DegradedModeContext);

const GRACE_PERIOD_SECONDS = 300; // 5 minutes

export const DegradedModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session } = useAuth();
  const [isDegraded, setIsDegraded] = useState(false);
  const [gracePeriodRemaining, setGracePeriodRemaining] = useState(0);
  const [degradedReason, setDegradedReason] = useState<string | null>(null);
  const gracePeriodTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasShownWarningRef = useRef(false);

  // Clean up timers
  const clearTimers = useCallback(() => {
    if (gracePeriodTimerRef.current) {
      clearTimeout(gracePeriodTimerRef.current);
      gracePeriodTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Enter degraded mode
  const enterDegradedMode = useCallback((reason: string) => {
    console.log('🔴 Entering degraded mode:', reason);
    setIsDegraded(true);
    setDegradedReason(reason);
    setGracePeriodRemaining(GRACE_PERIOD_SECONDS);

    // Show warning toast only once
    if (!hasShownWarningRef.current) {
      toast.warning(
        `Authentication issue detected: ${reason}\nYou have 5 minutes of read-only access before automatic logout.`,
        { duration: 10000 }
      );
      hasShownWarningRef.current = true;
    }

    // Clear any existing timers
    clearTimers();

    // Start countdown
    countdownIntervalRef.current = setInterval(() => {
      setGracePeriodRemaining((prev) => {
        if (prev <= 1) {
          // Grace period expired, force logout
          console.log('🔴 Grace period expired, forcing logout');
          clearTimers();

          // Clear localStorage sensitive data
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (
              key.startsWith('sb-') ||
              key.includes('supabase') ||
              key.includes('career_lens') ||
              key.includes('future_lens')
            )) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));

          // Show logout message
          toast.error('Session expired. Please log in again.', { duration: 8000 });

          // Redirect to login
          window.location.href = '/login';

          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearTimers]);

  // Exit degraded mode
  const exitDegradedMode = useCallback(() => {
    console.log('✅ Exiting degraded mode');
    setIsDegraded(false);
    setDegradedReason(null);
    setGracePeriodRemaining(0);
    hasShownWarningRef.current = false;
    clearTimers();
  }, [clearTimers]);

  // Monitor session health
  useEffect(() => {
    if (!session || !user) {
      // No session, not in degraded mode, just logged out
      if (isDegraded) {
        exitDegradedMode();
      }
      return;
    }

    // Session exists and is valid, exit degraded mode if we're in it
    if (isDegraded && session) {
      console.log('✅ Valid session detected, exiting degraded mode');
      exitDegradedMode();
    }
  }, [session, user, isDegraded, exitDegradedMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  const contextValue: DegradedModeContextType = {
    isDegraded,
    gracePeriodRemaining,
    enterDegradedMode,
    exitDegradedMode,
    canPerformWrite: !isDegraded, // Block writes in degraded mode
    canAccessSensitiveData: !isDegraded, // Block sensitive data access in degraded mode
    degradedReason,
  };

  return (
    <DegradedModeContext.Provider value={contextValue}>
      {children}
    </DegradedModeContext.Provider>
  );
};
